import type { Reservation } from '@/mocks/organizer';

interface Props {
  reservation: Reservation;
  onClose: () => void;
}

const formatPrice = (n: number) =>
  n === 0 ? 'Gratuit' : `${n.toLocaleString('fr-FR')} FCFA`;

const formatDateTime = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
    year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const STATUS_CONFIG = {
  confirmed: {
    label: 'Confirmé',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-700',
    icon: 'ri-checkbox-circle-fill',
    bannerMsg: 'Ce ticket est valide et prêt à être scanné à l\'entrée.',
  },
  pending: {
    label: 'Paiement non validé',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    dot: 'bg-gold',
    badge: 'bg-gold-100 text-gold-dark',
    icon: 'ri-time-fill',
    bannerMsg: 'Le client a démarré le paiement, mais il ne l’a pas encore finalisé. Ce ticket n’est pas validé.',
  },
  cancelled: {
    label: 'Annulé',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    text: 'text-gray-500',
    dot: 'bg-gray-400',
    badge: 'bg-gray-100 text-gray-500',
    icon: 'ri-close-circle-fill',
    bannerMsg: 'Cette réservation a été annulée et n\'est plus valide.',
  },
};

export default function TicketDetailPanel({ reservation, onClose }: Props) {
  const sc = STATUS_CONFIG[reservation.status];
  const qrData = `TICKET:${reservation.ticketCode}|EVENT:${reservation.eventId}|BUYER:${reservation.email}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrData)}&color=1e2a4a&bgcolor=ffffff&qzone=1`;
  const pricePerTicket = reservation.tickets > 0 ? reservation.total / reservation.tickets : reservation.total;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-gray-50 z-50 flex flex-col">

        {/* Header */}
        <div className="flex-shrink-0 bg-navy px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
              <i className="ri-ticket-2-line text-white text-base"></i>
            </div>
            <div>
              <p className="text-xs text-white/60 font-medium">Détail du ticket</p>
              <p className="text-sm font-bold text-white font-mono tracking-wide">{reservation.ticketCode}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Status banner */}
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${sc.bg} ${sc.border}`}>
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`}></div>
            <i className={`${sc.icon} ${sc.text} text-base flex-shrink-0`}></i>
            <p className={`text-xs font-medium ${sc.text} leading-relaxed`}>{sc.bannerMsg}</p>
          </div>

          {/* Ticket card */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">

            {/* Top section — event + buyer */}
            <div className="px-5 pt-5 pb-4 bg-gradient-to-br from-navy to-navy-lighter">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">Événement</p>
                  <h3 className="text-sm font-bold text-white leading-tight">{reservation.eventTitle}</h3>
                </div>
                <span className={`flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${sc.badge}`}>
                  {sc.label}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-full flex-shrink-0">
                  <i className="ri-user-line text-white text-sm"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white">{reservation.buyer}</p>
                  <p className="text-xs text-white/60">{reservation.email}</p>
                </div>
              </div>
            </div>

            {/* Perforated separator */}
            <div className="relative flex items-center">
              <div className="w-5 h-5 rounded-full bg-gray-50 flex-shrink-0 -ml-2.5 border-r border-gray-100"></div>
              <div className="flex-1 border-t-2 border-dashed border-gray-200 mx-1"></div>
              <div className="w-5 h-5 rounded-full bg-gray-50 flex-shrink-0 -mr-2.5 border-l border-gray-100"></div>
            </div>

            {/* QR code section */}
            <div className="px-5 py-5 flex flex-col items-center gap-3">
              <div className={`p-3 rounded-2xl border-2 ${reservation.status === 'cancelled' ? 'border-gray-200 opacity-40' : 'border-navy/10'} bg-white relative`}>
                {reservation.status === 'cancelled' && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 rounded-xl bg-white/80">
                    <div className="flex flex-col items-center gap-1">
                      <i className="ri-close-circle-fill text-4xl text-gray-400"></i>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Annulé</p>
                    </div>
                  </div>
                )}
                <img
                  src={qrUrl}
                  alt={`QR Code ${reservation.ticketCode}`}
                  className="w-44 h-44"
                  loading="lazy"
                />
              </div>
              <div className="text-center">
                <p className="font-mono text-sm font-bold text-navy tracking-widest">{reservation.ticketCode}</p>
                <p className="text-xs text-gray-400 mt-0.5">Présenter à l&apos;entrée de l&apos;événement</p>
              </div>
            </div>

            {/* Perforated separator */}
            <div className="relative flex items-center">
              <div className="w-5 h-5 rounded-full bg-gray-50 flex-shrink-0 -ml-2.5 border-r border-gray-100"></div>
              <div className="flex-1 border-t-2 border-dashed border-gray-200 mx-1"></div>
              <div className="w-5 h-5 rounded-full bg-gray-50 flex-shrink-0 -mr-2.5 border-l border-gray-100"></div>
            </div>

            {/* Details grid */}
            <div className="px-5 py-4 grid grid-cols-2 gap-3">
              {[
                { label: 'Tickets', value: `${reservation.tickets} ticket${reservation.tickets > 1 ? 's' : ''}`, icon: 'ri-ticket-line', color: 'text-navy' },
                { label: 'Prix unitaire', value: formatPrice(pricePerTicket), icon: 'ri-price-tag-3-line', color: 'text-navy' },
                { label: 'Total payé', value: formatPrice(reservation.total), icon: 'ri-money-cny-circle-line', color: 'text-emerald-600' },
                { label: 'Téléphone', value: reservation.phone, icon: 'ri-phone-line', color: 'text-navy' },
                { label: 'Adresse', value: reservation.address || 'Non renseigné', icon: 'ri-map-pin-line', color: 'text-navy' },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <i className={`${item.icon} text-xs text-gray-400`}></i>
                    <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{item.label}</p>
                  </div>
                  <p className={`text-sm font-bold ${item.color}`}>{item.value}</p>
                </div>
              ))}
            </div>

            {/* Date footer */}
            <div className="px-5 pb-5">
              <div className="bg-navy/5 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-navy rounded-lg flex-shrink-0">
                  <i className="ri-calendar-check-line text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Réservé le</p>
                  <p className="text-xs font-semibold text-navy capitalize">{formatDateTime(reservation.date)}</p>
                </div>
              </div>
            </div>

          </div>

          {/* Info tip */}
          <div className="flex items-start gap-2.5 bg-gold-100/70 rounded-xl px-4 py-3">
            <i className="ri-information-line text-gold text-base flex-shrink-0 mt-0.5"></i>
            <p className="text-xs text-gold-dark leading-relaxed">
              Ce QR code est unique et lié à cette réservation. Il sera scanné à l&apos;entrée pour valider l&apos;accès à l&apos;événement.
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-4 bg-white border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            Fermer
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-lighter cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-printer-line"></i>Imprimer
          </button>
        </div>

      </div>
    </>
  );
}
